import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiFetch } from './client'

/**
 * Admin provider flip — a single mutation hook wrapping
 * ``POST /api/admin/orgs/{org_id}/provider`` (Phase 5, PR #116).
 *
 * The endpoint flips an org's voice provider between ``vapi`` and ``retell``.
 * It's superadmin-only and refuses the flip while the org has any in-flight
 * call (409). See ``src/api/admin.py::set_org_provider`` for the contract.
 *
 * ── No "current provider" read hook here ────────────────────────────────────
 * There is intentionally NO query hook for an org's *current* provider:
 * nothing on the backend surfaces ``organizations.provider`` over HTTP. The
 * bulk ``/api/admin/orgs-provisioning`` list (``useAdminOrgsProvisioning``)
 * does not select that column, and there is no singular org-detail GET. Since
 * this is a UI-only change we don't add one — instead the flip response's
 * ``previous_provider`` / ``new_provider`` are the authoritative source the
 * control uses to display state after a flip. (See ``OrgProviderControl``.)
 *
 * The success handler still invalidates the orgs-provisioning + retell-phones
 * caches so that, the moment a future backend change DOES expose ``provider``
 * on those reads, the UI reflects the flip automatically with no edit here.
 */

// ── Shapes ─────────────────────────────────────────────────────────────────

export type VoiceProvider = 'vapi' | 'retell'

export interface FlipOrgProviderArgs {
  provider: VoiceProvider
}

export interface FlipOrgProviderResponse {
  org_id: string
  // ``previous_provider`` can be null for an org that never had the column set
  // (the backend normalizes "" → null). ``new_provider`` is always one of the
  // allowed values because the endpoint validates before writing.
  previous_provider: VoiceProvider | null
  new_provider: VoiceProvider
}

/**
 * Structured view of the 409 ``in_flight_calls`` rejection.
 *
 * The backend returns ``{detail: {error: "in_flight_calls", in_flight_count,
 * statuses}}``. Because ``apiFetch`` throws an :class:`ApiError` that already
 * parses the body and exposes ``.detail``, we read those fields directly — no
 * regex parsing like ``parsePhoneAssignError`` is needed.
 */
export interface InFlightCallsDetail {
  error: 'in_flight_calls'
  in_flight_count: number
  statuses: string[]
  message?: string
}

// ── Query keys (for invalidation only) ───────────────────────────────────────

// Mirror the keys defined in admin-phones.ts / admin-retell-phones.ts so a
// successful flip invalidates the same caches those hooks populate. Keeping
// the literals in sync (rather than importing private consts) matches how the
// other admin hooks are written.
const QK_ORGS_PROVISIONING = ['admin', 'phones', 'orgs-provisioning'] as const
const QK_RETELL_PHONES = (orgId: string) =>
  ['admin', 'retell-phones', orgId] as const

const ADMIN_BASE = '/api/admin'

// ── Mutation ──────────────────────────────────────────────────────────────

/**
 * Flip an org's voice provider.
 *
 * Bound to a single ``orgId`` so the call site reads
 * ``flip.mutate({ provider: 'retell' })``. On success we invalidate the
 * orgs-provisioning + this org's retell-phones queries so any provider-derived
 * UI refreshes.
 */
export function useFlipOrgProvider(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ provider }: FlipOrgProviderArgs) =>
      apiFetch<FlipOrgProviderResponse>(
        `${ADMIN_BASE}/orgs/${encodeURIComponent(orgId)}/provider`,
        {
          method: 'POST',
          body: JSON.stringify({ provider }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_ORGS_PROVISIONING })
      qc.invalidateQueries({ queryKey: QK_RETELL_PHONES(orgId) })
    },
  })
}

// ── Error helpers ──────────────────────────────────────────────────────────

/**
 * Pull the structured 409 ``in_flight_calls`` payload out of a thrown error.
 *
 * Returns the detail object when the error is a 409 carrying the
 * ``in_flight_calls`` shape, otherwise ``null`` (caller falls back to a
 * generic message).
 */
export function parseInFlightCallsError(
  err: unknown,
): InFlightCallsDetail | null {
  if (!(err instanceof ApiError)) return null
  if (err.status !== 409) return null
  const detail = err.detail as
    | { error?: unknown; in_flight_count?: unknown; statuses?: unknown; message?: unknown }
    | undefined
  if (!detail || detail.error !== 'in_flight_calls') return null
  return {
    error: 'in_flight_calls',
    in_flight_count:
      typeof detail.in_flight_count === 'number' ? detail.in_flight_count : 0,
    statuses: Array.isArray(detail.statuses)
      ? detail.statuses.filter((s): s is string => typeof s === 'string')
      : [],
    message: typeof detail.message === 'string' ? detail.message : undefined,
  }
}

/**
 * Best-effort human-readable message for any non-409 flip failure.
 *
 * Prefers the backend's ``detail.message`` (operator-facing copy from the
 * 400/404 branches), falling back to the raw ``Error.message``. Mirrors
 * ``getRetellApiErrorMessage`` in ``admin-retell-phones.ts``.
 */
export function getFlipErrorMessage(err: unknown): string | null {
  if (!(err instanceof Error)) return null
  if (err instanceof ApiError) {
    const detail = err.detail as { message?: unknown; error?: unknown } | undefined
    if (detail && typeof detail.message === 'string') return detail.message
    if (detail && typeof detail.error === 'string') return detail.error
  }
  return err.message
}
